import React from 'react';
import { CalendarPlus, Check, CircleHelp, Clock, Crosshair, MapPin, Navigation, Phone, X } from 'lucide-react';
import { api, resolveUrl, type HuntGameRowWithFee, type HuntInvitation as Invitation } from '../../api';
import { askForPush } from '../../push';
import { GAME_STATUS_LABEL } from '../../lib/skjutmeny';
import { GameStatusIcon } from '../../components/GameStatusIcon';
import { RADIUS, TYPE, useTheme } from '../../theme';
import { Badge, Button, Card, EmptyState, ErrorState, Icon, Label, Loader, Textarea } from '../../components/ui';
import { NVV_SEASONS, isInSeason } from '../../lib/season';
import { PlanMap, type MapMarker } from '../planera/PlanMap';
import { MARKER_LABEL, type MarkerType } from '../planera/planModel';
import { buildIcs, directionsUrl } from './invitationUtils';

// Inbjudan till en planerad jakt (J1). Samma vy används
//  · publikt på /inbjudan/:token (utan konto, länk från mejlet),
//  · i appen från Kalender → Visa inbjudan,
//  · som förhandsvisning i Planera jakt (preview: svarsraden är avstängd).

export type Answer = 'yes' | 'maybe' | 'no';
const ANSWERS: { key: Answer; label: string }[] = [{ key: 'yes', label: 'Ja' }, { key: 'maybe', label: 'Kanske' }, { key: 'no', label: 'Nej' }];
export const ANSWER_LABEL: Record<string, string> = { yes: 'Ja', maybe: 'Kanske', no: 'Nej', invited: 'Väntar' };
const SVAR: Record<string, Answer> = { ja: 'yes', kanske: 'maybe', nej: 'no' };
export const answerFromQuery = (svar: string | null | undefined): Answer | null => (svar ? SVAR[svar.toLowerCase()] ?? null : null);

const TZ = 'Europe/Stockholm';
const fmtDay = (iso: string) => { const s = new Intl.DateTimeFormat('sv-SE', { timeZone: TZ, weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(iso)); return s[0].toUpperCase() + s.slice(1); };
const fmtTime = (iso?: string | null) => (iso ? new Intl.DateTimeFormat('sv-SE', { timeZone: TZ, hour: '2-digit', minute: '2-digit' }).format(new Date(iso)) : '');

function planMarkers(inv: Invitation): MapMarker[] {
  try {
    const plan = inv.event.satPlanJson ? JSON.parse(inv.event.satPlanJson) : null;
    const out: MapMarker[] = [];
    for (const s of plan?.satar ?? []) {
      for (const m of s.markers ?? []) {
        if (!Number.isFinite(Number(m.lat))) continue;
        const type: MarkerType = m.type in MARKER_LABEL ? m.type : 'pass';
        const mine = !!inv.name && m.assignedName === inv.name;
        out.push({ id: String(m.id), type, lat: Number(m.lat), lng: Number(m.lng), label: m.label || MARKER_LABEL[type], assignedName: m.assignedName, highlight: mine });
      }
    }
    return out;
  } catch { return []; }
}

/** S1: markens såter som jakten gäller (planens såtar med markSatId). */
function satNamesOf(inv: Invitation): string[] {
  try { const p = inv.event.satPlanJson ? JSON.parse(inv.event.satPlanJson) : null; return (p?.satar ?? []).filter((s: any) => s?.markSatId).map((s: any) => String(s.namn || 'Såt')); } catch { return []; }
}

function gearOf(inv: Invitation): string[] {
  try { const p = inv.event.satPlanJson ? JSON.parse(inv.event.satPlanJson) : null; return Array.isArray(p?.utrustning) ? p.utrustning : []; } catch { return []; }
}

export function HuntInvitationView({ inv, onRespond, preselect, preview, compact, liveGameRows }: {
  inv: Invitation;
  /** Aktuellt läge i appen (räknat ur avskjutningsplanen nu); annars ögonblicksbilden från utskicket. */
  liveGameRows?: HuntGameRowWithFee[] | null;
  onRespond?: (status: Answer, comment: string) => Promise<void>;
  preselect?: Answer | null;
  preview?: boolean;
  compact?: boolean;
}) {
  const T = useTheme();
  const ev = inv.event;
  const cancelled = !!ev.cancelledAt || ev.status === 'cancelled';
  const markers = React.useMemo(() => planMarkers(inv), [inv]);
  const gear = gearOf(inv);
  const satNames = satNamesOf(inv);
  const meeting = ev.meetingLat != null && ev.meetingLng != null ? { lat: ev.meetingLat, lng: ev.meetingLng, label: ev.meetingPlace || 'Samlingsplats' } : null;
  const directions = directionsUrl(ev.meetingLat, ev.meetingLng, ev.meetingPlace);
  const legal = NVV_SEASONS.filter(s => isInSeason(s, new Date(ev.startTime)));
  const games = liveGameRows ?? inv.gameRows ?? [];
  const counts = inv.participants.reduce((a, p) => { a[p.status] = (a[p.status] ?? 0) + 1; return a; }, {} as Record<string, number>);
  const img = inv.mark.imageUrl ? resolveUrl(inv.mark.imageUrl) : null;
  const canAnswer = !!inv.token && !!onRespond && !preview && !cancelled && ev.status !== 'completed';

  const downloadIcs = () => {
    const blob = new Blob([buildIcs(inv, window.location.origin)], { type: 'text/calendar;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'jakt.ics';
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  const times = [
    { label: 'Samling', value: fmtTime(ev.meetingTime) },
    { label: 'Jaktstart', value: fmtTime(ev.startTime) },
    { label: 'Slut', value: fmtTime(ev.endTime) },
  ].filter(t => t.value);

  return (
    <div style={{ color: T.text, paddingBottom: canAnswer ? 0 : 8 }}>
      {/* Hero */}
      <div style={{ position: 'relative', minHeight: compact ? 120 : 180, borderRadius: compact ? RADIUS.md : RADIUS.lg, overflow: 'hidden', background: '#20281b', display: 'flex', alignItems: 'flex-end' }}>
        {img && <img src={img} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(20,28,16,.35) 0%, rgba(20,28,16,.88) 100%)' }} />
        <div style={{ position: 'relative', padding: compact ? '14px 14px 12px' : '18px 18px 16px', color: '#f1f2e9' }}>
          <div style={{ ...TYPE.label, color: '#f5b06c' }}>Inbjudan · {inv.mark.name}</div>
          <h1 style={{ margin: '6px 0 2px', fontSize: compact ? 20 : 26, fontWeight: 800, lineHeight: 1.2 }}>{ev.title}</h1>
          <div style={{ fontSize: 15, fontWeight: 500, color: '#dfe3d3' }}>{fmtDay(ev.startTime)} · {inv.hostName} bjuder in</div>
        </div>
      </div>

      {cancelled && <div style={{ marginTop: 12 }}><ErrorState title="Jakten är inställd" message={`${inv.hostName} har ställt in jakten.`} /></div>}

      {/* Tider */}
      {times.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${times.length}, minmax(0,1fr))`, gap: 8, marginTop: 12 }}>
          {times.map(t => (
            <div key={t.label} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: RADIUS.md, padding: '10px 12px' }}>
              <div style={{ ...TYPE.small, color: T.textMuted }}>{t.label}</div>
              <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em' }}>{t.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Ditt pass */}
      {inv.passLabel && (
        <div style={{ marginTop: 10, background: T.accentSoft, border: `1px solid ${T.accentFill}`, borderRadius: RADIUS.md, padding: '12px 14px', display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{ color: T.accentText }}><Icon as={Crosshair} size={22} /></span>
          <div>
            <div style={{ ...TYPE.label, color: T.accentText }}>Ditt pass</div>
            <div style={{ fontSize: 17, fontWeight: 800 }}>{inv.passLabel}</div>
          </div>
        </div>
      )}

      {/* Samlingsplats och karta */}
      <div style={{ marginTop: 12 }}>
        {(meeting || markers.length > 0) && (
          <PlanMap height={compact ? 160 : 240} centerLat={meeting?.lat ?? inv.mark.lat} centerLng={meeting?.lng ?? inv.mark.lng} markers={markers} meeting={meeting} ariaLabel="Karta med samlingsplats och pass" />
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
          <span style={{ color: T.textMuted }}><Icon as={MapPin} size={18} /></span>
          <span style={{ flex: 1, minWidth: 160, ...TYPE.body }}>{ev.meetingPlace || 'Samlingsplats på kartan'}</span>
          {directions && <Button size="sm" variant="ghost" icon={<Icon as={Navigation} size={18} />} onClick={() => window.open(directions, '_blank', 'noopener')} style={{ color: T.accentText, minHeight: 44 }}>Vägbeskrivning</Button>}
          {!preview && <Button size="sm" variant="ghost" icon={<Icon as={CalendarPlus} size={18} />} onClick={downloadIcs} style={{ color: T.accentText, minHeight: 44 }}>Lägg i kalendern</Button>}
        </div>
        {satNames.length > 0 && (
          <div data-testid="inbjudan-sater" style={{ ...TYPE.body, marginTop: 6 }}>
            <span style={{ color: T.textMuted }}>{satNames.length === 1 ? 'Såt: ' : 'Såter: '}</span>{satNames.join(', ')}
          </div>
        )}
      </div>

      {ev.message && (
        <section style={{ marginTop: 14 }}>
          <Label>Meddelande</Label>
          <div style={{ ...TYPE.bodyL, whiteSpace: 'pre-wrap', marginTop: 6 }}>{ev.message}</div>
        </section>
      )}

      {gear.length > 0 && (
        <section style={{ marginTop: 14 }}>
          <Label>Ta med</Label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>{gear.map(g => <Badge key={g} tone="neutral">{g}</Badge>)}</div>
        </section>
      )}

      {games.length > 0 ? (
        <section style={{ marginTop: 14 }}>
          <Label>Skjutmeny</Label>
          <GameTable rows={games} />
        </section>
      ) : !compact && legal.length > 0 && (
        // Jakter planerade före skjutmenyn i inbjudan: bara jakttiderna.
        <section style={{ marginTop: 14 }}>
          <Label>Lovligt vilt den dagen</Label>
          <div style={{ ...TYPE.small, color: T.textSub, marginTop: 6, lineHeight: 1.6 }}>{legal.map(g => g.name).join(', ')}</div>
        </section>
      )}

      <section style={{ marginTop: 14 }}>
        <Label>Deltagare · {counts.yes ?? 0} ja{counts.maybe ? ` · ${counts.maybe} kanske` : ''}{counts.no ? ` · ${counts.no} nej` : ''}{counts.invited ? ` · ${counts.invited} väntar` : ''}</Label>
        <div style={{ marginTop: 4 }}>
          {inv.participants.map((p, i) => (
            <div key={`${p.name}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, borderTop: i ? `1px solid ${T.border}` : 'none' }}>
              <span style={{ flex: 1, minWidth: 0, ...TYPE.body }}>
                {p.name}
                <span style={{ ...TYPE.small, color: T.textMuted }}>{[p.isHost ? 'jaktledare' : null, p.isGuest ? 'gäst' : null, p.passLabel].filter(Boolean).map(s => ` · ${s}`).join('')}</span>
              </span>
              <StatusMark status={p.status} />
            </div>
          ))}
        </div>
      </section>

      {(ev.contactName || ev.contactPhone) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 48, marginTop: 8, borderTop: `1px solid ${T.border}` }}>
          <span style={{ color: T.textMuted }}><Icon as={Phone} size={18} /></span>
          <span style={{ flex: 1, ...TYPE.body }}>Kontakt {ev.contactName}</span>
          {ev.contactPhone && <a href={`tel:${ev.contactPhone.replace(/\s/g, '')}`} style={{ color: T.accentText, fontWeight: 700, fontSize: 15, minHeight: 44, display: 'inline-flex', alignItems: 'center' }}>{ev.contactPhone}</a>}
        </div>
      )}

      {canAnswer && <RespondBar inv={inv} onRespond={onRespond!} preselect={preselect ?? null} />}
      {preview && (
        <div style={{ marginTop: 14, padding: 12, borderRadius: RADIUS.md, background: T.surface2 }}>
          <div style={{ ...TYPE.small, color: T.textMuted, marginBottom: 8 }}>Mottagarna svarar här</div>
          <div style={{ display: 'flex', gap: 8 }}>{ANSWERS.map((a, i) => <Button key={a.key} block variant={i ? 'secondary' : 'primary'} disabled>{a.label}</Button>)}</div>
        </div>
      )}
    </div>
  );
}

/** Skjutmenyn: viltslag, kvar i planen, status och fällavgift – kompakt, en rad per viltslag. */
function GameTable({ rows }: { rows: HuntGameRowWithFee[] }) {
  const T = useTheme();
  const color = { lovligt: T.ok, fel_tid: T.amber, slut: T.danger, okand: T.textMuted, ej_jakttid: T.textMuted } as const;
  const kr = (r: HuntGameRowWithFee) => (r.fee != null ? `${r.feeFrom ? 'från ' : ''}${Math.round(r.fee).toLocaleString('sv-SE')} kr` : '–');
  const th: React.CSSProperties = { ...TYPE.label, fontSize: 11, color: T.textMuted, textAlign: 'left', padding: '6px 8px 6px 0' };
  return (
    <table data-testid="skjutmeny" style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6, fontSize: 14 }}>
      <thead><tr><th style={th}>Viltslag</th><th style={th}>Kvar</th><th style={th}>Status</th><th style={{ ...th, textAlign: 'right', paddingRight: 0 }}>Fällavgift</th></tr></thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.gameType} style={{ borderTop: `1px solid ${T.border}` }}>
            <td style={{ padding: '8px 8px 8px 0', fontWeight: 600 }}>{r.gameType}</td>
            <td style={{ padding: '8px 8px 8px 0', color: T.textSub, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{r.planned != null && r.remaining != null ? `${r.remaining} kvar av ${r.planned}` : '–'}</td>
            <td title={r.reason ?? undefined} style={{ padding: '8px 8px 8px 0', color: color[r.status] ?? T.textMuted, fontWeight: 700, whiteSpace: 'nowrap' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><GameStatusIcon status={r.status} T={T} size={12} />{GAME_STATUS_LABEL[r.status] ?? r.status}</span>
              {r.status === 'okand' && r.reason && <div style={{ fontWeight: 400, fontSize: 12, color: T.textMuted, whiteSpace: 'normal' }}>{r.reason.replace(/^Jakttid okänd: /, '')}</div>}
            </td>
            <td style={{ padding: '8px 0', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{kr(r)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function StatusMark({ status }: { status: string }) {
  const T = useTheme();
  if (status === 'yes') return <span title="Ja" style={{ color: T.ok, display: 'inline-flex', alignItems: 'center', gap: 4, ...TYPE.small, fontWeight: 700 }}><Icon as={Check} size={18} />Ja</span>;
  if (status === 'no') return <span title="Nej" style={{ color: T.danger, display: 'inline-flex', alignItems: 'center', gap: 4, ...TYPE.small, fontWeight: 700 }}><Icon as={X} size={18} />Nej</span>;
  if (status === 'maybe') return <span title="Kanske" style={{ color: T.info, display: 'inline-flex', alignItems: 'center', gap: 4, ...TYPE.small, fontWeight: 700 }}><Icon as={CircleHelp} size={18} />Kanske</span>;
  return <span title="Väntar på svar" style={{ color: T.textMuted, display: 'inline-flex', alignItems: 'center', gap: 4, ...TYPE.small }}><Icon as={Clock} size={16} />Väntar</span>;
}

/** Fast svarsrad längst ned. Ett svar från mejlets länk är förvalt men måste skickas med ett tryck. */
function RespondBar({ inv, onRespond, preselect }: { inv: Invitation; onRespond: (s: Answer, c: string) => Promise<void>; preselect: Answer | null }) {
  const T = useTheme();
  const answered = inv.status && inv.status !== 'invited' ? (inv.status as Answer) : null;
  const [editing, setEditing] = React.useState(!answered || (!!preselect && preselect !== answered));
  const [choice, setChoice] = React.useState<Answer | null>(preselect ?? answered);
  const [comment, setComment] = React.useState(inv.comment ?? '');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  const send = async () => {
    if (!choice) return;
    setBusy(true); setErr(null);
    try { await onRespond(choice, comment); setEditing(false); } catch (e) { setErr((e as Error).message || 'Svaret kunde inte sparas'); }
    setBusy(false);
  };

  return (
    // Fast längst ned bara så länge man inte har svarat – annars döljer den kartan och passet.
    <div data-testid="respond-bar" style={{ position: !editing && answered ? 'static' : 'sticky', bottom: 0, marginTop: 16, marginInline: -16, padding: '12px 16px calc(12px + env(safe-area-inset-bottom, 0px))', background: T.surface, borderTop: `1px solid ${T.border}`, boxShadow: T.shadow.sheet, zIndex: 2 }}>
      {!editing && answered ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, ...TYPE.body }}>Du har svarat <strong>{ANSWER_LABEL[answered]}</strong>{inv.comment ? <span style={{ color: T.textMuted }}> · ”{inv.comment}”</span> : null}</div>
          <Button variant="secondary" onClick={() => setEditing(true)}>Ändra svar</Button>
        </div>
      ) : (
        <>
          <div style={{ ...TYPE.small, color: T.textMuted, marginBottom: 8 }}>{preselect && choice === preselect && !answered ? 'Bekräfta ditt svar' : 'Kommer du?'}</div>
          <div role="radiogroup" aria-label="Ditt svar" style={{ display: 'flex', gap: 8 }}>
            {ANSWERS.map(a => {
              const on = choice === a.key;
              return (
                <button key={a.key} type="button" role="radio" aria-checked={on} onClick={() => setChoice(a.key)}
                  style={{ flex: 1, minHeight: 48, borderRadius: RADIUS.md, fontSize: 16, fontWeight: 800, fontFamily: 'inherit', cursor: 'pointer',
                    background: on ? T.accentFill : T.surface, color: on ? T.onAccent : T.text, border: `1px solid ${on ? T.accentFill : T.borderHover}` }}>
                  {a.label}
                </button>
              );
            })}
          </div>
          {choice && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <Textarea aria-label="Kommentar (valfritt)" rows={1} placeholder="Kommentar (valfritt), t.ex. tar med hund" value={comment} onChange={e => setComment(e.target.value)} style={{ flex: '1 1 220px', minHeight: 44 }} />
              <Button onClick={send} loading={busy} style={{ flex: '0 0 auto' }}>Skicka svar</Button>
            </div>
          )}
          {err && <div role="alert" style={{ color: T.danger, ...TYPE.small, marginTop: 6 }}>{err}</div>}
        </>
      )}
    </div>
  );
}

/** /inbjudan/:token — fungerar utan inloggning. */
export function InvitationPage({ token, svar }: { token: string; svar?: string | null }) {
  const T = useTheme();
  const [inv, setInv] = React.useState<Invitation | null | undefined>(undefined);
  const [err, setErr] = React.useState<string | null>(null);
  React.useEffect(() => {
    api.huntInvitation(token).then(setInv).catch(e => setErr((e as Error).message));
  }, [token]);
  return (
    <div style={{ minHeight: '100dvh', background: T.bg, color: T.text }}>
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '16px 16px 0' }}>
        <a href="/" style={{ ...TYPE.label, color: T.accentText, textDecoration: 'none', display: 'inline-flex', minHeight: 44, alignItems: 'center' }}>Qvenari</a>
        {err ? <ErrorState title="Inbjudan kunde inte visas" message={err} />
          : inv === undefined ? <Loader text="Hämtar inbjudan…" />
          : inv === null ? <Card><EmptyState icon={X} title="Inbjudan finns inte" sub="Länken kan vara fel, eller så har jaktledaren tagit bort dig från jakten." /></Card>
          : <HuntInvitationView inv={inv} preselect={answerFromQuery(svar)} onRespond={async (s, c) => { setInv(await api.respondToHuntInvitation(token, s, c)); void askForPush(); }} />}
      </div>
    </div>
  );
}
